// src/pages/affectations/AffectationControleurDetail.js
import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import Layout from "../components/Layout";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  "http://localhost/securecontrol-backend";

const COLORS = {
  primary: "#0B3C5D",
  secondary: "#1E88C8",
  accent: "#4FC3F7",
  darkText: "#2E2E2E",
  lightBg: "#F5F7FA",
  white: "#FFFFFF",
  danger: "#f44336",
  success: "#4caf50",
  warning: "#ff9800",
  info: "#2196f3",
  successLight: "#e8f5e9",
  dangerLight: "#ffebee",
  warningLight: "#fff3e0",
  infoLight: "#e3f2fd",
};

const formatDate = (dateString) => {
  if (!dateString || dateString === "0000-00-00 00:00:00") return "-";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return dateString;
  }
};

const AffectationControleurDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [affectation, setAffectation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [selectedSite, setSelectedSite] = useState(null);
  const [removeLoading, setRemoveLoading] = useState(false);

  const fetchDetail = useCallback(async () => {
    if (!id || isNaN(parseInt(id))) {
      setError("ID d'affectation invalide");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        setError("Session expirée. Veuillez vous reconnecter.");
        setTimeout(() => navigate("/login"), 2000);
        setLoading(false);
        return;
      }

      const response = await axios.get(
        `${API_BASE_URL}/affectations/detail_affectation_controleur.php?id_affectation=${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
          timeout: 10000,
        },
      );

      if (response.data?.success) {
        const data = response.data.data;
        setAffectation(data);
        // Si l'affectation n'a plus de sites, rediriger vers la liste
        if (!data.sites || data.sites.length === 0) {
          navigate("/affectations-controleurs");
        }
      } else {
        setError(
          response.data?.message || "Impossible de charger les détails.",
        );
      }
    } catch (err) {
      console.error("Erreur détail affectation:", err);
      if (err.response) {
        switch (err.response.status) {
          case 400:
            setError("ID invalide.");
            break;
          case 401:
            setError("Session expirée.");
            localStorage.removeItem("access_token");
            setTimeout(() => navigate("/login"), 2000);
            break;
          case 403:
            setError("Accès interdit.");
            break;
          case 404:
            setError("Affectation non trouvée.");
            break;
          default:
            setError(
              `Erreur ${err.response.status}: ${err.response.data?.message || "Erreur serveur"}`,
            );
        }
      } else if (err.request) {
        setError("Serveur inaccessible.");
      } else {
        setError("Erreur lors de la requête.");
      }
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  // Ouvre la modal de confirmation pour retirer un site
  const handleRemoveClick = (site) => {
    setSelectedSite(site);
    setShowRemoveModal(true);
  };

  // Confirmation et appel API
  const confirmRemove = async () => {
    if (!selectedSite || !affectation?.controleur?.id_user) {
      alert("Impossible d'identifier le site ou le contrôleur.");
      setShowRemoveModal(false);
      return;
    }

    const token = localStorage.getItem("access_token");
    if (!token) {
      alert("Session expirée. Veuillez vous reconnecter.");
      navigate("/login");
      return;
    }

    setRemoveLoading(true);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/affectations/delete-controleur.php`,
        {
          id_user: affectation.controleur.id_user,
          id_site: selectedSite.id_site,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (response.data?.success) {
        setShowRemoveModal(false);
        setSelectedSite(null);

        // Si c'était le dernier site, rediriger vers la liste sans message
        if (affectation.sites.length === 1) {
          navigate("/affectations-controleurs");
        } else {
          fetchDetail(); // Recharger les données (il reste d'autres sites)
        }
      } else {
        alert(response.data?.message || "Erreur lors de la suppression.");
        setShowRemoveModal(false);
      }
    } catch (err) {
      console.error("Erreur suppression site:", err);
      let errorMsg = "Erreur lors de la suppression.";
      if (err.response) {
        errorMsg =
          err.response.data?.message || `Erreur ${err.response.status}`;
      } else if (err.request) {
        errorMsg = "Serveur inaccessible.";
      } else {
        errorMsg = err.message;
      }
      alert(errorMsg);
      setShowRemoveModal(false);
    } finally {
      setRemoveLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: "600" }}>
            Détail de l'affectation
          </h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/dashboard" style={{ color: COLORS.primary }}>
                  Tableau de bord
                </Link>
              </li>
              <li className="breadcrumb-item">
                <Link
                  to="/affectations-controleurs"
                  style={{ color: COLORS.secondary }}
                >
                  Affectations Contrôleurs
                </Link>
              </li>
              <li
                className="breadcrumb-item active"
                style={{ color: COLORS.secondary }}
              >
                Chargement...
              </li>
            </ol>
          </nav>
        </div>
        <section className="section">
          <div
            className="card"
            style={{
              borderRadius: "12px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
            }}
          >
            <div className="card-body text-center py-5">
              <div
                className="spinner-border"
                role="status"
                style={{ color: COLORS.primary }}
              />
              <p className="mt-2" style={{ color: COLORS.darkText }}>
                Chargement des détails...
              </p>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  if (error || !affectation) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: "600" }}>
            Détail de l'affectation
          </h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/dashboard" style={{ color: COLORS.primary }}>
                  Tableau de bord
                </Link>
              </li>
              <li className="breadcrumb-item">
                <Link
                  to="/affectations-controleurs"
                  style={{ color: COLORS.secondary }}
                >
                  Affectations Contrôleurs
                </Link>
              </li>
              <li
                className="breadcrumb-item active"
                style={{ color: COLORS.secondary }}
              >
                Erreur
              </li>
            </ol>
          </nav>
        </div>
        <section className="section">
          <div
            className="card"
            style={{
              borderRadius: "12px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
            }}
          >
            <div className="card-body">
              <div
                className="alert alert-danger d-flex align-items-center"
                style={{
                  backgroundColor: "#ffebee",
                  borderColor: COLORS.danger,
                  color: "#c62828",
                  borderRadius: "8px",
                  borderLeft: `4px solid ${COLORS.danger}`,
                }}
              >
                <i className="bi bi-exclamation-triangle me-2"></i>
                <div className="flex-grow-1">
                  {error || "Données non disponibles"}
                </div>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setError("")}
                />
              </div>
              <div className="text-center">
                <button
                  className="btn fw-semibold"
                  onClick={() => navigate("/affectations-controleurs")}
                  style={{
                    color: COLORS.primary,
                    borderColor: COLORS.primary,
                    backgroundColor: "transparent",
                    borderRadius: "8px",
                    padding: "10px 24px",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = COLORS.primary;
                    e.target.style.color = "white";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "transparent";
                    e.target.style.color = COLORS.primary;
                  }}
                >
                  <i className="bi bi-arrow-left me-2"></i> Retour aux
                  affectations
                </button>
              </div>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  const { controleur, sites } = affectation;

  return (
    <Layout>
      <div className="pagetitle">
        <h1 style={{ color: COLORS.primary, fontWeight: "600" }}>
          Détail de l'affectation
        </h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/dashboard" style={{ color: COLORS.primary }}>
                Tableau de bord
              </Link>
            </li>
            <li className="breadcrumb-item">
              <Link
                to="/affectations-controleurs"
                style={{ color: COLORS.primary }}
              >
                Affectations Contrôleurs
              </Link>
            </li>
            <li
              className="breadcrumb-item active"
              style={{ color: COLORS.secondary }}
            >
              Details
            </li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div
          className="card"
          style={{
            borderRadius: "12px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
            border: "none",
          }}
        >
          <div className="card-body">
            {/* Titre de la carte principale */}
            <h5
              className="card-title mb-4"
              style={{
                color: COLORS.primary,
                borderBottom: `2px solid ${COLORS.lightBg}`,
                paddingBottom: "16px",
              }}
            >
              <div className="d-flex align-items-center">
                <div
                  className="rounded-circle d-inline-flex align-items-center justify-content-center me-3"
                  style={{
                    width: "36px",
                    height: "36px",
                    backgroundColor: "#E3F2FD",
                    color: COLORS.primary,
                  }}
                >
                  <i className="bi bi-person-lines-fill"></i>
                </div>
                Détails de l'affectation
              </div>
            </h5>

            {/* Informations du contrôleur */}
            {controleur && (
              <div
                className="card mb-4"
                style={{
                  border: `1px solid ${COLORS.lightBg}`,
                  borderRadius: "10px",
                }}
              >
                <div
                  className="card-header"
                  style={{
                    backgroundColor: COLORS.lightBg,
                    borderBottom: `1px solid ${COLORS.lightBg}`,
                    padding: "12px 16px",
                  }}
                >
                  <h6
                    className="card-title mb-0"
                    style={{ color: COLORS.primary, fontWeight: "600" }}
                  >
                    <i
                      className="bi bi-shield-check me-2"
                      style={{ color: COLORS.secondary }}
                    ></i>
                    Contrôleur affecté
                  </h6>
                </div>
                <div className="card-body">
                  <div className="d-flex align-items-center">
                    <div className="me-3">
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center fw-semibold"
                        style={{
                          width: "60px",
                          height: "60px",
                          fontSize: "20px",
                          backgroundColor: "#fff3e0",
                          color: "#f57c00",
                        }}
                      >
                        {controleur.prenom_user?.charAt(0) ||
                          controleur.nom_user?.charAt(0) ||
                          "C"}
                      </div>
                    </div>
                    <div>
                      <h5 className="mb-1">
                        {controleur.prenom_user} {controleur.nom_user}
                      </h5>
                      {controleur.email_user && (
                        <p className="mb-1">
                          <i className="bi bi-envelope me-1 text-muted"></i>
                          {controleur.email_user}
                        </p>
                      )}
                      {controleur.phone_user && (
                        <p className="mb-0">
                          <i className="bi bi-telephone me-1 text-muted"></i>
                          {controleur.phone_user}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Liste des sites affectés */}
            <div
              className="card"
              style={{
                border: `1px solid ${COLORS.lightBg}`,
                borderRadius: "10px",
              }}
            >
              <div
                className="card-header d-flex justify-content-between align-items-center"
                style={{
                  backgroundColor: COLORS.lightBg,
                  borderBottom: `1px solid ${COLORS.lightBg}`,
                  padding: "12px 16px",
                }}
              >
                <h6
                  className="card-title mb-0"
                  style={{ color: COLORS.primary, fontWeight: "600" }}
                >
                  <i
                    className="bi bi-building me-2"
                    style={{ color: COLORS.secondary }}
                  ></i>
                  Sites affectés ({sites?.length || 0})
                </h6>
              </div>
              <div className="card-body p-0">
                {!sites || sites.length === 0 ? (
                  <p className="text-muted p-3">Aucun site affecté.</p>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover mb-0">
                      <thead style={{ backgroundColor: COLORS.lightBg }}>
                        <tr>
                          <th>Nom du site</th>
                          <th>Adresse</th>
                          <th>Statut</th>
                          <th>Date d'affectation</th>
                          <th className="text-end">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sites.map((site) => (
                          <tr key={site.id_site}>
                            <td>{site.nom_site}</td>
                            <td
                              style={{ maxWidth: "300px" }}
                              className="text-truncate"
                            >
                              {site.addresse_site}
                            </td>
                            <td>
                              <span
                                className={`badge ${site.statut_site === 1 ? "bg-success" : "bg-secondary"}`}
                              >
                                {site.statut_site === 1 ? "Actif" : "Inactif"}
                              </span>
                            </td>
                            <td>{formatDate(site.date_affectation)}</td>
                            <td className="text-end">
                              <Link
                                to={`/sites/details/${site.id_site}`}
                                className="btn btn-sm fw-semibold me-2"
                                style={{
                                  color: COLORS.primary,
                                  borderColor: COLORS.primary,
                                  backgroundColor: "transparent",
                                  borderRadius: "6px",
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.backgroundColor =
                                    COLORS.primary;
                                  e.target.style.color = "white";
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.backgroundColor =
                                    "transparent";
                                  e.target.style.color = COLORS.primary;
                                }}
                              >
                                <i className="bi bi-eye"></i> Voir
                              </Link>
                              <button
                                className="btn btn-sm fw-semibold"
                                onClick={() => handleRemoveClick(site)}
                                disabled={removeLoading}
                                style={{
                                  color: COLORS.danger,
                                  borderColor: COLORS.danger,
                                  backgroundColor: "transparent",
                                  borderRadius: "6px",
                                  opacity: removeLoading ? 0.6 : 1,
                                  cursor: removeLoading
                                    ? "not-allowed"
                                    : "pointer",
                                }}
                                onMouseEnter={(e) => {
                                  if (!removeLoading) {
                                    e.target.style.backgroundColor =
                                      COLORS.danger;
                                    e.target.style.color = "white";
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.backgroundColor =
                                    "transparent";
                                  e.target.style.color = COLORS.danger;
                                }}
                              >
                                <i className="bi bi-x-circle"></i> Rétirer
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Modal de confirmation de retrait */}
      {showRemoveModal && selectedSite && (
        <>
          <div className="modal-backdrop fade show"></div>
          <div
            className="modal fade show d-block"
            tabIndex="-1"
            style={{ display: "block" }}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div
                className="modal-content"
                style={{
                  borderRadius: "12px",
                  boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
                  border: "none",
                }}
              >
                <div
                  className="modal-header"
                  style={{
                    backgroundColor: "#ffebee",
                    borderBottom: `2px solid ${COLORS.danger}`,
                    borderRadius: "12px 12px 0 0",
                  }}
                >
                  <h5
                    className="modal-title"
                    style={{ color: "#c62828", fontWeight: "600" }}
                  >
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    Confirmer le retrait
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowRemoveModal(false)}
                    disabled={removeLoading}
                  ></button>
                </div>
                <div className="modal-body">
                  <p style={{ color: COLORS.darkText }}>
                    Êtes-vous sûr de vouloir retirer le site suivant de cette
                    affectation ?
                  </p>
                  <div
                    className="alert alert-warning"
                    style={{
                      backgroundColor: "#fff3e0",
                      borderColor: "#ff9800",
                      color: "#ff6f00",
                      borderRadius: "8px",
                    }}
                  >
                    <div className="d-flex align-items-center">
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center me-3"
                        style={{
                          width: "32px",
                          height: "32px",
                          backgroundColor: "#E3F2FD",
                          color: COLORS.primary,
                        }}
                      >
                        <i className="bi bi-building"></i>
                      </div>
                      <div>
                        <strong>{selectedSite.nom_site}</strong>
                        <br />
                        <small>{selectedSite.addresse_site}</small>
                      </div>
                    </div>
                  </div>
                  <p className="text-muted small" style={{ color: "#6c757d" }}>
                    <i className="bi bi-info-circle me-1"></i>
                    Cette action est irréversible. Le contrôleur ne sera plus
                    affecté à ce site.
                  </p>
                </div>
                <div
                  className="modal-footer"
                  style={{ borderTop: `1px solid ${COLORS.lightBg}` }}
                >
                  <button
                    className="btn fw-semibold"
                    onClick={() => setShowRemoveModal(false)}
                    disabled={removeLoading}
                    style={{
                      color: COLORS.primary,
                      borderColor: COLORS.primary,
                      backgroundColor: "transparent",
                      borderRadius: "8px",
                      padding: "10px 20px",
                    }}
                    onMouseEnter={(e) => {
                      if (!removeLoading) {
                        e.target.style.backgroundColor = "#E3F2FD";
                        e.target.style.borderColor = COLORS.secondary;
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!removeLoading) {
                        e.target.style.backgroundColor = "transparent";
                        e.target.style.borderColor = COLORS.primary;
                      }
                    }}
                  >
                    Annuler
                  </button>
                  <button
                    className="btn fw-semibold"
                    onClick={confirmRemove}
                    disabled={removeLoading}
                    style={{
                      color: COLORS.white,
                      backgroundColor: COLORS.danger,
                      borderColor: COLORS.danger,
                      borderRadius: "8px",
                      padding: "10px 24px",
                    }}
                    onMouseEnter={(e) => {
                      if (!removeLoading) {
                        e.target.style.backgroundColor = "#d32f2f";
                        e.target.style.borderColor = "#d32f2f";
                        e.target.style.transform = "translateY(-2px)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!removeLoading) {
                        e.target.style.backgroundColor = COLORS.danger;
                        e.target.style.borderColor = COLORS.danger;
                        e.target.style.transform = "translateY(0)";
                      }
                    }}
                  >
                    {removeLoading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                        ></span>
                        Retrait...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-x-circle me-2"></i>
                        Retirer le site
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      <style>{`
        .pagetitle h1,
        .card-title,
        .btn,
        .modal-title,
        .alert strong,
        .table th {
          font-family: 'Inter', sans-serif;
        }
        
        .card-body,
        .modal-body,
        .form-control,
        .form-select,
        .form-text,
        .alert,
        .table td,
        small {
          font-family: 'Roboto', sans-serif;
        }
        
        .modal-backdrop {
          z-index: 1040;
          background-color: rgba(0, 0, 0, 0.5);
        }
        
        .modal {
          z-index: 1050;
        }
        
        .table-hover tbody tr:hover {
          background-color: ${COLORS.lightBg};
          transition: background-color 0.2s ease;
        }
        
        .btn:focus,
        .form-control:focus,
        .page-link:focus {
          outline: none;
          box-shadow: 0 0 0 0.2rem rgba(30, 136, 200, 0.25);
        }
        
        .btn,
        .badge {
          transition: all 0.2s ease-in-out;
        }
        
        .btn:disabled {
          cursor: not-allowed;
          opacity: 0.6;
        }
        
        .form-control:focus {
          border-color: ${COLORS.secondary};
          box-shadow: 0 0 0 0.2rem rgba(30, 136, 200, 0.25);
        }
        
        @media (max-width: 768px) {
          .card {
            margin: 0 -16px;
            border-radius: 0 !important;
          }
          
          .pagetitle h1 {
            font-size: 1.5rem;
          }
          
          .d-flex.justify-content-between {
            flex-direction: column;
            gap: 16px;
          }
          
          .d-flex.justify-content-between > div {
            width: 100%;
          }
          
          .btn {
            width: 100%;
            margin-bottom: 8px;
          }
          
          .table-responsive {
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
          }
          
          .table th,
          .table td {
            padding: 12px 8px !important;
          }
          
          .modal-dialog {
            margin: 16px;
          }
          
          .col-md-4 {
            margin-bottom: 16px;
          }
        }
        
        /* Amélioration de la lisibilité des tables */
        .table th {
          text-transform: uppercase;
          letter-spacing: 0.5px;
          font-size: 0.85rem;
        }
        
        .table td {
          vertical-align: middle;
        }
        
        /* Animation pour les badges */
        .badge:hover {
          transform: translateY(-1px);
        }
      `}</style>
    </Layout>
  );
};

export default AffectationControleurDetail;
